import React, { useEffect, useState } from "react";
import { ClientApiProps } from "../common";
import {
  Link,
  LinkProps,
  Navigate,
  Route,
  Routes,
  useMatch,
  useNavigate,
  useResolvedPath,
} from "react-router-dom";
import { Spinner } from "acma-spa";
import { Client, IMNO, ISettings, ProhibitedAlphaTag, getApiClient } from "../api";
import { RegisterPage } from "./Register";
import { AlphaTagPage } from "./AlphaTags";
import { AuthorisationPage } from "./Authorisations";
import { SettingsPage } from "./Settings";
import { SettingsStore } from "../stores/SettingsStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FirewallPage } from "./Firewall";
import { Consent } from "./Consent";

const Tab = ({ children, to, id }: LinkProps) => {
  let resolved = useResolvedPath(to);
  let match = useMatch({ path: resolved.pathname, end: true });

  return (
    <li className="nav-item">
      <Link
        id={id}
        to={to}
        className={`nav-link${match ? " active" : ""}`}
        style={{ backgroundImage: "none" }}
      >
        {children}
      </Link>
    </li>
  );
};

export interface LayoutProps extends ClientApiProps { }

export const Layout: React.FC<LayoutProps> = (props) => {
  const [spinner, setSpinner] = useState("Loading...");
  const [settings, setSettings] = useState<ISettings[]>([]);
  const [mnos, setMNOs] = useState<IMNO[]>([]);
  const [implementers, setImplementers] = useState<IMNO[]>([]);
  const [consent, setConsent] = useState(true);
  const [readonly, setReadonly] = useState(false);

  const load = async (client: Client) => {
    setSpinner("Loading...");

    try {
      var store = new SettingsStore(client);
      const newSettings = await store.getSettings();
      setSettings(newSettings.settings);
      setMNOs(newSettings.mnOs ?? []);
      setImplementers(newSettings.implementers ?? []);
      setConsent(newSettings.consent ?? false);
    } finally {
      setSpinner("");
    }
  };

  useEffect(() => {
    const [client, abort] = getApiClient(
      props.baseUrl,
      props.b2cDomainName,
      props.msalClient
    );

    load(client);

    return function () {
      abort.abort();
    };
  }, []);

  var defaultPath: string | undefined;

  const isParticipant = settings.filter((r) => r.isParticipant).length > 0;
  const isRepresentative = settings.filter((r) => r.isRepresentative).length > 0;
  const isMno = settings.filter((r) => r.isMno).length > 0;

  if (isMno) defaultPath = "/senderid/firewall";
  else if (isParticipant) defaultPath = "/senderid/register";
  else if (isRepresentative) defaultPath = "/senderid/my";

  const tabs = [
    { path: "/senderid/register", title: "Alpha tag list", description: "Validate a token you have received, or request confirmation for new alpha tags in the registry.", visible: isParticipant },
    { path: "/senderid/firewall", title: "Firewall list", description: "View and export alpha tags that are subject to firewall rules.", visible: isMno },
    { path: "/senderid/my", title: "Alpha tags", description: "Add and manage your alpha tags.", visible: isRepresentative },
    { path: "/senderid/confirmations", title: "Confirmations", description: "Confirm valid use case of your alpha tags by generating a token to send to SMS service providers. You can also confirm or revoke requests received from SMS service providers to use your alpha tags.", visible: isRepresentative },
    { path: "/senderid/settings", title: "Settings", description: `Manage your details for receiving ${isRepresentative && "confirmation requests" || ""}${(isRepresentative && (isParticipant || isMno)) && " / " || ""}${(isParticipant || isMno) && "notifications" || ""}${isParticipant && " and set up system to system integration" || ""}.`, visible: true },
  ].filter(r => r.visible);

  return (
    consent ? <>
      <h1>Sender ID registry</h1>
      <Spinner isLoading={spinner ? true : false} loadingText={spinner}>
        <p>Click <a href="#" onClick={e => {
        e.preventDefault();
        setReadonly(true);
        setConsent(false);
      }}>here</a> to view Terms of Use</p>
        <p>
          Use this dashboard to securely {!isRepresentative ? "manage" : (isParticipant || isMno) && isRepresentative ? "register your / manage" : "register your"} SMS sender IDs (alpha
          tags).
        </p>
        {tabs.map((r, i) => <p key={i}>
          <b>{i + 1}. {r.title}</b> – {r.description}
        </p>)}
        <p>Note you can select a maximum of 10 alpha tags to bulk action.</p>
        <ul className="nav nav-tabs mb-3">
          {tabs.map((r, i) => <Tab id={`register${i + 1}`} to={r.path} key={i}>
            <>
              {(r.path == "/senderid/settings" && settings.filter((r) => r.isRepresentative && !r.requestMailbox).length > 0) ?
                <FontAwesomeIcon
                  icon={"exclamation-triangle"}
                  size={"1x"}
                  className="text-danger"
                  title="Mailbox to recieve confirmation requests has not been specified."
                /> : <></>}
              {" "} {i + 1}. {r.title}
            </>
          </Tab>)}
        </ul>
        <Routes>
          <Route
            path="register"
            element={
              <RegisterPage
                {...props}
                settings={settings}
                onSettings={setSettings}
              />
            }
          />
          <Route
            path="firewall"
            element={<FirewallPage {...props} mnos={mnos} settings={settings} implementers={implementers} />}
          />
          <Route
            path="my"
            element={<AlphaTagPage {...props} mnos={mnos} settings={settings} />}
          />
          <Route
            path="confirmations"
            element={<AuthorisationPage {...props} />}
          />
          <Route
            path="settings"
            element={
              <SettingsPage
                {...props}
                settings={settings}
                onSettings={setSettings}
                onSpinner={setSpinner}
              />
            }
          />
          <Route
            path="*"
            element={defaultPath ? <Navigate to={defaultPath} replace={true} /> : <></>}
          />
        </Routes>
      </Spinner>
    </> : <Consent {...props} readonly={readonly} onChange={() => setConsent(true)} />
  );
};
